// src/services/SignOutService.js
//
// "Log out" that logs out.
//
// The nav's Log out used to call the old PrivacyFirstAuth class's
// logout(true), which removed one localStorage key and nothing else: no
// Firebase signOut(), no end to the hub session. On a family iPad or a school
// laptop the account stayed signed in across a reload, and the next person
// could open the first person's grief entries and unsent letters. The fix for
// that in #215 was written into src/services/PrivacyFirstAuth.js, a file the
// app never loaded, and went when that file was deleted in 2c160eaa4.
//
// Signing out now, in this order:
//   1. Push anything still waiting to sync, so the last few seconds of writing
//      reach the backup before the device copy goes (anonymous accounts only;
//      a few seconds at most).
//   2. Stop the sync queue, so nothing pushes after this point.
//   3. Sign out of the auth hub AND this project (HubSessionService.endHubSession).
//   4. Remove this account's toolkit entries, and the cached identity the app
//      keeps, from this device.
// The caller then reloads the page, so nothing of this person's is left in
// memory either.
//
// Australian English throughout.

import { clearToolScopes } from '../utils/localData';

// The identity the React app caches, and the flags login.html and signup.html
// leave behind. With these present PrivacyAuthContext would restore the user
// from cache even after Firebase has signed out.
export const IDENTITY_KEYS = ['vibe_user_identity', 'vibe_auth_success', 'vibe_user_id', 'vibe_user'];

const FLUSH_TIMEOUT_MS = 4000;

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

async function defaults() {
  const hub = await import('./HubSessionService');
  const sync = await import('./ToolSyncService');
  return {
    ownership: hub.sessionOwnership,
    endSessions: hub.endHubSession,
    flush: sync.flushToHub,
    resetSync: sync.resetSyncState,
  };
}

/** What signing out will do on this device: 'hub', 'native' or 'none' (see sessionOwnership). */
export async function signOutKind() {
  try {
    const { sessionOwnership } = await import('./HubSessionService');
    return (await sessionOwnership()).kind;
  } catch {
    return 'none';
  }
}

/**
 * Sign out of the hub and this app, and clear this account's toolkit entries
 * from this device. Never throws.
 *
 * Returns { kind, cleared: [keys removed] }.
 */
export async function signOutEverywhere(deps = {}) {
  const d = { ...(await defaults()), ...deps };
  const local = deps.local ?? (typeof localStorage !== 'undefined' ? localStorage : undefined);

  let owner = { kind: 'none', hubUid: null, spokeUid: null };
  try { owner = await d.ownership(); } catch { /* treat as nobody */ }

  // The app can also be showing a user restored from its own cached identity
  // (PrivacyAuthContext does this while Firebase restores, or when it can't);
  // that account's entries go too.
  let cachedUid = null;
  try { cachedUid = JSON.parse(local?.getItem('vibe_user_identity') || 'null')?.userId || null; } catch { /* not JSON */ }

  if (owner.kind === 'hub') {
    try { await withTimeout(d.flush(), FLUSH_TIMEOUT_MS); } catch { /* the local copy goes either way */ }
  }
  try { d.resetSync(); } catch { /* nothing queued */ }

  try { await d.endSessions(); } catch { /* endHubSession already swallows its own errors */ }

  const cleared = clearToolScopes([owner.spokeUid, owner.hubUid, cachedUid], { local });
  for (const key of IDENTITY_KEYS) {
    try {
      if (local && local.getItem(key) !== null) {
        local.removeItem(key);
        cleared.push(key);
      }
    } catch { /* storage unavailable */ }
  }

  return { kind: owner.kind, cleared };
}

export default { signOutEverywhere, signOutKind, IDENTITY_KEYS };
